import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { assignmentAPI, groupAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function GroupLectureAssignments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { groupId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [assignments, setAssignments] = useState([]);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    complexity: searchParams.get('complexity') || '',
    language: searchParams.get('language') || '',
    search: searchParams.get('search') || ''
  });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [viewingAssignment, setViewingAssignment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  // Update URL parameters when filters change
  useEffect(() => {
    const params = {};
    if (currentPage > 1) params.page = currentPage.toString();
    if (filters.type) params.type = filters.type;
    if (filters.complexity) params.complexity = filters.complexity;
    if (filters.language) params.language = filters.language;
    if (filters.search) params.search = filters.search;
    
    setSearchParams(params, { replace: true });
  }, [currentPage, filters, setSearchParams]);

  useEffect(() => {
    fetchAssignments();
  }, [groupId, currentPage, filters]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await assignmentAPI.getLectureAssignmentsByGroup(groupId, {
        page: currentPage,
        limit: 20,
        ...filters
      });
      
      setAssignments(response.data.assignments);
      setPagination(response.data.pagination);
      setGroup(response.data.group);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch assignments', 'error');
      if (error.response?.status === 404) {
        navigate('/admin/groups');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleViewDetails = (assignment) => {
    setViewingAssignment(assignment);
    setShowDetailModal(true);
  };

  const getTypeLabel = (type) => {
    const labels = {
      concept_reinforcement: '📚 Concept Reinforcement',
      debugging: '🐛 Debugging',
      small_coding: '💻 Small Coding',
      feature_implementation: '⚙️ Feature Implementation',
      output_prediction: '🔮 Output Prediction',
      mini_project: '🚀 Mini Project'
    };
    return labels[type] || type;
  };

  const getComplexityColor = (complexity) => {
    return complexity === 'Hard' ? '#ef4444' : complexity === 'Medium' ? '#f59e0b' : '#10b981';
  };

  if (loading && !assignments.length) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <div style={{ fontSize: '30px', marginBottom: '12px', animation: 'spin 2s linear infinite' }}>⟳</div>
            <p>Loading assignments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1a202c', fontWeight: '700' }}>
              📚 Lecture Assignments
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
              {group?.name} • Transcript-based Assignments
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/groups')}
            style={{
              padding: '10px 20px',
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ← Back to Groups
          </button>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: '1', minWidth: '250px', display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                flex: 1,
                padding: '10px 14px',
                fontSize: '14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px'
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '10px 20px',
                background: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Search
            </button>
          </div>

          {/* Type Filter */}
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            style={{
              padding: '10px 14px',
              fontSize: '14px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              minWidth: '180px'
            }}
          >
            <option value="">All Types</option>
            <option value="concept_reinforcement">Concept Reinforcement</option>
            <option value="debugging">Debugging</option>
            <option value="small_coding">Small Coding</option>
            <option value="feature_implementation">Feature Implementation</option>
            <option value="output_prediction">Output Prediction</option>
            <option value="mini_project">Mini Project</option>
          </select>

          {/* Complexity Filter */}
          <select
            value={filters.complexity}
            onChange={(e) => handleFilterChange('complexity', e.target.value)}
            style={{
              padding: '10px 14px',
              fontSize: '14px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              minWidth: '140px'
            }}
          >
            <option value="">All Levels</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>

          {/* Language Filter */}
          <select
            value={filters.language}
            onChange={(e) => handleFilterChange('language', e.target.value)}
            style={{
              padding: '10px 14px',
              fontSize: '14px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              minWidth: '140px'
            }}
          >
            <option value="">All Languages</option>
            <option value="javascript">JavaScript</option>
            <option value="python">Python</option>
            <option value="java">Java</option>
            <option value="sql">SQL</option>
            <option value="csharp">C#</option>
            <option value="typescript">TypeScript</option>
          </select>
        </div>

        {/* Stats */}
        {pagination && (
          <div style={{ 
            marginBottom: '20px', 
            padding: '12px 16px', 
            background: '#f8fafc', 
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            color: '#64748b'
          }}>
            <span>
              Showing {(currentPage - 1) * 20 + 1} - {Math.min(currentPage * 20, pagination.totalAssignments)} of {pagination.totalAssignments} assignments
            </span>
            <span style={{ fontWeight: '600', color: '#3b82f6' }}>
              Page {currentPage} of {pagination.totalPages}
            </span>
          </div>
        )}

        {/* Assignments Grid */}
        {assignments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No assignments found</p>
            <p style={{ fontSize: '14px' }}>
              {filters.search || filters.type || filters.complexity || filters.language
                ? 'Try adjusting your filters'
                : 'No transcript-based assignments have been created yet'
              }
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
            {assignments.map((assignment) => (
              <div
                key={assignment._id}
                onClick={() => handleViewDetails(assignment)}
                style={{
                  padding: '20px',
                  background: '#ffffff',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  ':hover': {
                    borderColor: '#3b82f6',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header */}
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                    {assignment.title}
                  </h3>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#ffffff',
                      background: '#3b82f6',
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      {getTypeLabel(assignment.type).split(' ').slice(1).join(' ')}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#ffffff',
                      background: getComplexityColor(assignment.complexity),
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      {assignment.complexity}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#6b7280',
                      background: '#f3f4f6',
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      {assignment.language?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                  {assignment.description?.substring(0, 120)}{assignment.description?.length > 120 ? '...' : ''}
                </p>

                {/* Source Asset */}
                <div style={{ 
                  padding: '8px 12px', 
                  background: '#f8fafc', 
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>
                    Source Session:
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
                    📝 {assignment.sourceAsset?.title || 'N/A'}
                  </div>
                </div>

                {/* Meta Info */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#64748b',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <span>⏱️ {assignment.estimatedTime}</span>
                  <span>📅 {new Date(assignment.createdAt).toLocaleDateString()}</span>
                </div>

                {/* Active Statement Indicator */}
                {assignment.activeProblemStatement?.version && assignment.activeProblemStatement.version !== 'original' && (
                  <div style={{
                    marginTop: '8px',
                    padding: '4px 8px',
                    background: '#f0fdf4',
                    border: '1px solid #86efac',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#166534',
                    textAlign: 'center'
                  }}>
                    ⭐ Active: {assignment.activeProblemStatement.version} statement
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                background: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                color: currentPage === 1 ? '#cbd5e1' : '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              ← Previous
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, idx) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{
                      padding: '8px 12px',
                      background: currentPage === pageNum ? '#3b82f6' : '#ffffff',
                      color: currentPage === pageNum ? '#ffffff' : '#475569',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      minWidth: '40px'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
              disabled={currentPage === pagination.totalPages}
              style={{
                padding: '8px 16px',
                background: currentPage === pagination.totalPages ? '#f1f5f9' : '#ffffff',
                color: currentPage === pagination.totalPages ? '#cbd5e1' : '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && viewingAssignment && (
        <Modal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          aria-labelledby="assignment-detail-modal"
          disableScrollLock={true}
          BackdropProps={{ timeout: 0 }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '95%', sm: '90%', md: '900px' },
              maxHeight: '90vh',
              bgcolor: '#ffffff',
              borderRadius: '12px',
              boxShadow: 24,
              p: 3,
              overflowY: 'auto',
              outline: 'none'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '700', color: '#1e293b' }}>
                  {viewingAssignment.title}
                </h2>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#ffffff',
                    background: '#3b82f6',
                    padding: '4px 10px',
                    borderRadius: '5px'
                  }}>
                    {getTypeLabel(viewingAssignment.type)}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#ffffff',
                    background: getComplexityColor(viewingAssignment.complexity),
                    padding: '4px 10px',
                    borderRadius: '5px'
                  }}>
                    {viewingAssignment.complexity}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    padding: '4px 10px',
                    borderRadius: '5px'
                  }}>
                    {viewingAssignment.language?.toUpperCase()}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    padding: '4px 10px',
                    borderRadius: '5px'
                  }}>
                    ⏱️ {viewingAssignment.estimatedTime}
                  </span>
                </div>
              </div>
              <IconButton onClick={() => setShowDetailModal(false)} size="small">
                <CloseIcon />
              </IconButton>
            </div>

            {/* Source Asset */}
            <div style={{ 
              marginBottom: '20px',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                SOURCE SESSION
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                📝 {viewingAssignment.sourceAsset?.title || 'N/A'}
              </div>
              {viewingAssignment.sourceAsset?.description && (
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {viewingAssignment.sourceAsset.description}
                </div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                📋 Description
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                {viewingAssignment.description}
              </p>
            </div>

            {/* Active Problem Statement */}
            {viewingAssignment.activeProblemStatement?.content && (
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                background: '#f0fdf4',
                border: '2px solid #86efac',
                borderRadius: '8px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#166534' }}>
                  ⭐ Active Problem Statement ({viewingAssignment.activeProblemStatement.version})
                </h3>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#1e293b', 
                  lineHeight: '1.7',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {viewingAssignment.activeProblemStatement.content}
                </div>
              </div>
            )}

            {/* Original Problem Statement */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                📄 Original Problem Statement
              </h3>
              <div style={{ 
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {viewingAssignment.problemStatement}
              </div>
            </div>

            {/* Objectives */}
            {viewingAssignment.objectives && viewingAssignment.objectives.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                  🎯 Learning Objectives
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#475569', lineHeight: '1.8' }}>
                  {viewingAssignment.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Test Cases */}
            {viewingAssignment.testCases && viewingAssignment.testCases.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                  ✅ Test Cases
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {viewingAssignment.testCases.slice(0, 5).map((tc, idx) => (
                    <div key={idx} style={{
                      padding: '12px',
                      background: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#3b82f6', marginBottom: '6px' }}>
                        Test Case {idx + 1}
                      </div>
                      <div style={{ fontSize: '13px', color: '#1e293b', marginBottom: '4px' }}>
                        <strong>Input:</strong> <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '3px' }}>{tc.input}</code>
                      </div>
                      <div style={{ fontSize: '13px', color: '#1e293b' }}>
                        <strong>Expected:</strong> <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '3px' }}>{tc.expectedOutput}</code>
                      </div>
                      {tc.explanation && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', fontStyle: 'italic' }}>
                          {tc.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                  {viewingAssignment.testCases.length > 5 && (
                    <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                      +{viewingAssignment.testCases.length - 5} more test cases
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Starter Code */}
            {viewingAssignment.starterCode && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                  🚀 Starter Code
                </h3>
                <pre style={{
                  margin: 0,
                  padding: '12px',
                  background: '#1e293b',
                  color: '#e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  overflowX: 'auto',
                  maxHeight: '300px'
                }}>
                  {viewingAssignment.starterCode}
                </pre>
              </div>
            )}

            {/* Metadata */}
            <div style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <span>Created: {new Date(viewingAssignment.createdAt).toLocaleString()}</span>
              <span>Updated: {new Date(viewingAssignment.updatedAt).toLocaleString()}</span>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
}

export default GroupLectureAssignments;
